<template>
  <div id="qmi-detail">
    <MoleculesLoadingOverlay v-if="loading" />
    <div v-else>
      <OrganismsProductDetailBW :organism="brandWindow" />
      <OrganismsFlexibleFeatures
        v-if="flexibleFeatures.item"
        :organism="flexibleFeatures"
      />
      <OrganismsWysiwygBlock
        v-if="home?.qmiHomeDetails?.propertyDescription"
        :organism="{ description: home?.qmiHomeDetails?.propertyDescription }"
      />
      <OrganismsTabbedContent
        id="explore-floorplan"
        v-if="tabbedContent.defaultTabHash"
        :organism="tabbedContent"
        class="mb-20"
      >
        <template v-for="item in tabbedContent.item" :key="item.id" #[item.id]>
          <img
            class="mx-auto mb-5"
            :src="item.image.src"
            :alt="item.image.alt"
          />
          <MoleculesButtons
            :buttons="tabbedContent.button"
            :button-colors="tabbedContent.button.colors"
            class="!justify-center mx-auto"
          />
        </template>
      </OrganismsTabbedContent>
      <OrganismsGalleryGrid v-if="galleryGrid" :organism="galleryGrid" />
      <OrganismsVirtualTour
        id="virtual-tour"
        v-if="virtualTour.virtualTourUrl"
        class="mx-auto my-24 bg-gray-900"
        :organism="virtualTour"
      />
      <OrganismsMapWithInfo v-if="mapLoaded" :organism="mapWithInfo" />
      <OrganismsTextCTA
        id="cta-be-the-first"
        :organism="beTheFirst"
        class="my-16"
      />
      <OrganismsModalForm>
        <header class="mb-4">
          <h2 class="mb-4 text-4xl font-semibold text-black font-figtree">
            Get More Info About This Home
          </h2>
          <p v-if="!isFormSubmitted" class="text-lg text-gray-500">
            Fill out the form below and we'll be in touch with more information
            about this home. To be the first to find out about news from Consort
            Homes, be sure to sign-up for our newsletter as well.
          </p>
        </header>
        <FormsDynamicGravityForm :gfid="8" />
      </OrganismsModalForm>
      <OrganismsFormCTA
        :organism="formCTA"
        gfid="8"
        class="bg-gradient-to-b from-bc-gray to-white"
      />
      <!--<OrganismsHeadingTransition
        :organism="{
          title1: 'DESIGN, DEDICATION & VALUE CONSORT HOMES',
          arrow: true,
        }"
        class="bg-gradient-to-b from-bc-gray to-white"
      />
      <OrganismsFlexibleContentColumns
        :organism="bottomWidgets"
        class="my-16"
      />-->
    </div>
  </div>
</template>

<style>
#qmi-detail {
  .virtual-tour {
    @apply bg-secondary-800;
    h2 {
      @apply text-white;
    }
  }

  .wysiwyg-block .text-content {
    @apply text-base text-gray-500 leading-relaxed max-w-2xl mx-auto mb-16;

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      @apply text-2xl sm:text-3xl text-gray-900 font-semibold font-figtree mt-4 mb-2;
    }

    p {
      @apply mt-0 mb-4;
    }

    a {
      @apply text-primary-500 hover:underline;
    }

    ul {
      @apply list-disc pl-5 mt-0 mb-4;
    }
    ol {
      @apply list-decimal pl-5 mt-0 mb-4;
    }
    li {
      @apply mb-2;
    }

    img {
      @apply my-4 mx-auto;
    }

    table {
      @apply min-w-full divide-y divide-gray-300;
    }
    th {
      @apply bg-gray-100 p-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider;
    }
    td {
      @apply p-2 text-sm text-gray-900;
    }

    blockquote {
      @apply border-l-4 border-gray-200 pl-4 italic text-gray-600;
    }

    code {
      @apply bg-gray-100 rounded p-1 font-mono text-sm;
    }

    hr {
      @apply my-4 border-gray-300;
    }
  }

  .tabbed-content {
    header {
      background-image: linear-gradient(101deg, #2d3142ff 0%, #4f5d75ff 97%);
      .header-content {
        @apply max-w-[556px];
      }
    }
    .tabs-component-tabs {
      @apply mx-auto flex-col md:flex-row justify-center;

      .tabs-component-tab {
        @apply md:max-w-[190px];

        .tabs-component-tab-a {
          @apply whitespace-nowrap;
        }
      }
    }
    .tabs-component-panels {
      .buttons a {
        @apply text-[15px] uppercase font-figtree font-medium py-3 px-10;
      }
    }
  }

  .form-cta {
    form {
      label {
        @apply text-white;
      }
    }
  }

  #product-detail-brand-window-text-content {
    .buttons {
      @apply block;

      .button-2-wrapper {
        @apply ml-0;
      }

      .button-2 {
        @apply text-center cursor-pointer mt-4 text-sm flex items-center justify-center w-full px-8 py-3 font-medium text-white border border-transparent shadow bg-secondary-500 rounded-md hover:bg-secondary-500/80 md:px-10;
      }

      .button-3-wrapper {
        @apply ml-0;
      }

      .button-3 {
        @apply text-center cursor-pointer mt-4 text-sm flex items-center justify-center w-full px-8 py-3 font-medium text-white border border-transparent shadow bg-accent-900 rounded-md hover:bg-accent-900/80 md:px-10;
      }
    }
  }

  .swiper-pagination {
    @apply bottom-12;
  }
}
</style>

<script setup>
const route = useRoute();
//import { useQuery } from "@vue/apollo-composable";
import { useAsyncQuery } from "#imports";
import { GET_QMI } from "~/graphql/GetQMI";
const { data, pending, error } = await useAsyncQuery(
  GET_QMI,
  { uri: route.path },
  { fetchPolicy: "no-cache" },
);
import { storeToRefs } from "pinia";
const breadcrumbs = useBreadcrumbs();
const { breadcrumbsList } = storeToRefs(breadcrumbs);
const modalForm = useModalForm();
const isFormSubmitted = ref(false);

const home = computed(() => {
  return data?.value?.home ?? [];
});

const mapLoaded = ref(false);

const loading = pending;

const computedBreadcrumbs = computed(() => {
  const crumbs = [];
  if (
    home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.neighborhood
      ?.title
  ) {
    crumbs.push({
      title:
        home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.neighborhood
          ?.title,
      link: home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails
        ?.neighborhood?.uri,
    });
    crumbs.push({
      title: "Quick Move-In Homes",
      link:
        home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.neighborhood
          ?.uri + "#quick-move-in",
    });
  } else {
    crumbs.push({
      title: "Quick Move-In Homes",
      link: "/our-homes/quick-move-in-homes/",
    });
  }
  crumbs.push({
    title: home?.value?.title,
  });

  return crumbs;
});

watch(computedBreadcrumbs, () => {
  breadcrumbsList.value = computedBreadcrumbs.value;
});

const brandWindow = computed(() => {
  const eciLink = home?.value?.qmiHomeDetails?.eciLink;
  const brochureLink = home?.value?.qmiHomeDetails?.brochureLink;
  return {
    pageType: "qmi-detail",
    collection:
      home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.collection ??
      home?.value?.collection?.collection ??
      "",
    title: home?.value?.qmiHomeDetails?.floorplan?.title ?? home?.value?.title,
    type: home?.value?.qmiHomeDetails?.type ?? "",
    neighborhoodTitle: home?.value?.qmiHomeDetails?.neighborhood?.title,
    neighborhoodLink: home?.value?.qmiHomeDetails?.neighborhood?.uri,
    address1: home?.value?.qmiHomeDetails?.address1,
    address2: home?.value?.qmiHomeDetails?.address2,
    city:
      home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.neighborhood
        ?.neighborhood?.city ?? "",
    state:
      home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.neighborhood
        ?.neighborhood?.state ?? "",
    zip:
      home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.neighborhood
        ?.neighborhood?.zip ?? "",
    latitude: home?.value?.qmiHomeDetails?.latitude,
    longitude: home?.value?.qmiHomeDetails?.longitude,
    bedsMin: home?.value?.qmiHomeDetails?.bedrooms,
    bathsMin: home?.value?.qmiHomeDetails?.bathrooms,
    disclaimer: home?.value?.qmiHomeDetails?.disclaimer,
    garageMin: home?.value?.qmiHomeDetails?.garage,
    sqftMin: home?.value?.qmiHomeDetails?.mainSqrFt,
    sqftMax: home?.value?.qmiHomeDetails?.finishedSqrFt,
    price: home?.value?.qmiHomeDetails?.price,
    autoplaySlider: true,
    autoplayDelay: 10000,
    sliderImages: home?.value?.qmiHomeDetails?.elevations,
    button1: !!tabbedContent.value.defaultTabHash,
    button1Text: "View Floorplan",
    link1: "#explore-floorplan",
    buttonColors: ["button-primary"],
    button2: !!eciLink,
    button2Text: "Interactive Floorplan",
    button2Target: "_blank",
    button2Color: ["button-blue"],
    link2: eciLink,
    button3: !!brochureLink,
    button3Text: "Included Features",
    button3Target: "_blank",
    button3Color: ["button-white"],
    link3: brochureLink,
  };
});

const collectionFeatures = computed(() => {
  let parsedJSON = {};
  try {
    parsedJSON = JSON.parse(data?.value?.collectionFeatures);
  } catch (e) {
    //console.log("Invalid collectionFeatures JSON:", e);
    return {};
  }
  return parsedJSON;
});

const flexibleFeatures = computed(() => {
  const collection =
    home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.collection ??
    home?.value?.collection?.collection ??
    "";
  let collectionTitle = "";
  let featuresList = [];

  switch (true) {
    case collection?.toLowerCase().includes("heritage"):
      collectionTitle = "Heritage";
      featuresList = collectionFeatures?.value?.heritage ?? [];
      break;
    case collection?.toLowerCase().includes("hometown"):
      collectionTitle = "Hometown";
      featuresList = collectionFeatures?.value?.hometown ?? [];
      break;
  }

  return {
    title1: collectionTitle + " Features",
    item: featuresList,
  };
});

const formCTA = {
  containerWidth: "full-width",
  title1: "Curious about this home?",
  description: `<p>Whether you're just getting started or ready to schedule a tour, our online team is here to help.</p><p>Call <a style="color:#d5990e" href="tel:6364898384">636-489-8384</a> or send us a message.</p>`,
  image: {
    sourceUrl: "/img/osc_consort_homes.png",
  },
};

const tabbedContent = computed(() => {
  const tabs = Array.isArray(home?.value?.qmiHomeDetails?.tabs)
    ? home?.value?.qmiHomeDetails?.tabs.map((tab, currentIndex, array) => {
        let tabName = tab.title;
        // let titleCount = 0;

        array.forEach(function (checkTab, checkIndex, checkArray) {
          if (tab.title === checkTab.title) {
            // titleCount++;

            if (currentIndex > checkIndex) {
              tabName = tab.title + " - Opt";
            }
          }
        });

        return {
          name: tabName,
          id: kebabCase(tabName),
          image: {
            src: getImageSrc(tab.image, "large"),
            alt: tab.image?.altText,
          },
        };
      })
    : [];

  return {
    containerWidth: "default",
    title1: "Explore this floorplan",
    title2: home?.value?.qmiHomeDetails?.floorplan?.title,
    item: tabs,
    defaultTabHash: tabs[0]?.id,
    variant: "product-detail",
    button: {
      colors: ["button-yellow"],
      button1: true,
      button1Text: "Get Brochure",
      button1Target: "_blank",
      link1: home?.value?.qmiHomeDetails?.pdfEPub,
    },
  };
});

const galleryGrid = computed(() => {
  if (!Array.isArray(home?.value?.qmiHomeDetails?.galleryItems)) {
    return false;
  }
  return {
    disclaimer: home?.value?.qmiHomeDetails?.disclaimer,
    gallery: home?.value?.qmiHomeDetails?.galleryItems.reduce(
      (imageArray, item) => {
        if (item.type === "image") {
          imageArray.push(item.image);
          return imageArray;
        }
      },
      [],
    ),
  };
});

const virtualTour = computed(() => {
  const collection =
    home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.collection ??
    home?.value?.collection?.collection ??
    "";
  const collectionSpace = collection ? collection + " " : "";

  const showButton1 = false;

  return {
    title1: home?.value?.qmiHomeDetails?.matterportTourTitle,
    title2: "Tour this " + collectionSpace + " home",
    virtualTourEmbedType: "url",
    virtualTourUrl: home?.value?.qmiHomeDetails?.matterportTour,
    button1: showButton1,
    button1Text: "Learn More",
    link1: home?.value?.qmiHomeDetails?.floorplan?.uri,
    buttonColors: ["button-gray"],
  };
});

const mapWithInfo = computed(() => {
  const neighborhood =
    home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.neighborhood;
  const qmi_neighborhood =
    home?.value?.qmiHomeDetails?.neighborhood?.neighborhood;
  const neighborhoodTitles = neighborhood?.[0]?.title;
  const neighborhoodCities = neighborhood?.[0]?.neighborhood?.city;
  const neighborhoodStates = neighborhood?.[0]?.neighborhood?.state;
  const neighborhoodZips = neighborhood?.[0]?.neighborhood?.zip;
  const neighborhoodHours = neighborhood?.[0]?.neighborhood?.hours;
  const neighborhoodPhones = neighborhood?.[0]?.neighborhood?.phone;
  const neighborhoodLegals = neighborhood?.[0]?.neighborhood?.legal;
  return {
    markerTitle: neighborhoodTitles,
    title1: "Stop by for a visit",
    address1: home?.value?.qmiHomeDetails?.address1,
    address2: home?.value?.qmiHomeDetails?.address2,
    //city: neighborhoodCities,
    //state: neighborhoodStates,
    //zip: neighborhoodZips,
    latitude: home?.value?.qmiHomeDetails?.latitude,
    longitude: home?.value?.qmiHomeDetails?.longitude,
    hours: qmi_neighborhood?.hours,
    phone: qmi_neighborhood?.phone,
    salesTeamMembers: qmi_neighborhood?.saleTeamMembers,
    legal: neighborhoodLegals,
  };
});

watch(loading, async (newLoading, oldLoading) => {
  const urlHash = window.location.hash;

  if (urlHash && oldLoading && !newLoading) {
    await nextTick();
    document.querySelector(urlHash).scrollIntoView(false);
  }
});

const beTheFirst = computed(() => {
  return {
    title1: "Become a VIP",
    description: `<p>Join our VIP List and be first in line to hear about new Consort Homes neighborhoods, new home plans, and events.</p>`,
    link1: "#modal-form",
    button1: true,
    button1Text: "Get Updates",
  };
});

const bottomWidgets = computed(() => {
  return {
    item: [
      {
        imageLink: "/where-we-build/",
        fieldGroupName: "item",
        image: {
          sourceUrl:
            "https://consortadmin.kinsta.cloud/wp-content/uploads/2024/01/View-Our-Neighborhoods_Widget-1024x948.png",
        },
      },
      {
        imageLink: "/contact-us/our-locations/",
        fieldGroupName: "item",
        image: {
          sourceUrl:
            "https://consortadmin.kinsta.cloud/wp-content/uploads/2024/01/widget-Need-Help-1024x948.jpg",
        },
      },
      {
        imageLink: "/our-homes/quick-move-in-homes/",
        fieldGroupName: "item",
        image: {
          sourceUrl:
            "https://consortadmin.kinsta.cloud/wp-content/uploads/2024/01/Quick-Move-Ins_Widget-1024x948.png",
        },
      },
    ],
  };
});

onMounted(() => {
  window.addEventListener(
    "hashchange",
    () => {
      if (window.location.hash === "#modal-form") {
        modalForm.openModal();
        window.location.hash = "";
      }
    },
    false,
  );

  mapLoaded.value = true;

  addTriggers();
});

const addTriggers = () => {
  const modalFormTriggers = document.querySelectorAll(
    "#cta-be-the-first .button-1, #cta-be-the-first a[href='#modal-form']",
  );

  modalFormTriggers.forEach((node) => {
    // Clone & replace node to remove existing event listeners
    const clonedNode = node.cloneNode(true);
    node.parentNode.replaceChild(clonedNode, node);

    clonedNode.addEventListener("click", (event) => {
      // Add event listener
      event.preventDefault();
      modalForm.openModal();
    });
  });
};

onUpdated(() => {
  addTriggers();
});
</script>
